import { useMemo } from 'react';
import { doc, type Firestore, type FirestoreError } from 'firebase/firestore';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { requireTenantId } from '@elka/firebase';

/** The `tenants/{tenantId}` registry document. */
export interface TenantDoc {
  name: string;
  branding?: {
    companyName?: string;
    logoStoragePath?: string;
    pdfFooter?: string;
    stampStoragePath?: string;
  };
  features?: {
    gmail?: boolean;
    publicDashboard?: boolean;
  };
  allowedDomains?: string[];
}

export interface UseTenantReturn {
  tenant: TenantDoc | null;
  /** Feature flags. Empty ({}) while loading — fail-closed. */
  features: { gmail?: boolean; publicDashboard?: boolean };
  isLoading: boolean;
  error: FirestoreError | undefined;
}

/**
 * Live-reads the current session tenant's registry document (`tenants/{id}`)
 * for branding + feature flags. The tenant is fixed per session, so the doc ref
 * is stable. Takes the app's Firestore instance since each app initializes
 * Firebase bespoke (only `team` uses `initializeFirebaseApp`).
 */
export function useTenant(db: Firestore): UseTenantReturn {
  const tenantId = requireTenantId();
  const ref = useMemo(() => doc(db, 'tenants', tenantId), [db, tenantId]);

  const [data, isLoading, error] = useDocumentData(ref);
  const tenant = (data as TenantDoc | undefined) ?? null;

  // Fail-closed while loading: expose no flags until the doc resolves.
  const features = isLoading ? {} : (tenant?.features ?? {});

  return { tenant, features, isLoading, error };
}
